import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CategoriesService from '../services/categoriesService';
import { lang } from '../config/lang';
import { motion } from 'framer-motion';
import SafeImage from '../components/SafeImage';

const CategoryPage = ({ category }) => {
  const { slug } = useParams();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedSubcategories, setSelectedSubcategories] = useState({});
  const [showGlobalComment, setShowGlobalComment] = useState(false);
  const [globalComment, setGlobalComment] = useState('');
  const [location, setLocation] = useState('');
  const [budget, setBudget] = useState('');
  const [timing, setTiming] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [descriptionMode, setDescriptionMode] = useState('categories');
  const [resolvedCategory, setResolvedCategory] = useState(category || null);

  const normalizeSlug = (s) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (category) {
          setResolvedCategory(category);
          return;
        }
        const all = await CategoriesService.getAllCategories();
        const found = all.find(c => normalizeSlug(c.nom) === slug);
        if (found) {
          const data = {
            title: found.nom,
            image: found.image,
            description: found.description || `Services professionnels de ${found.nom.toLowerCase()}`,
            subcategories: (found.sous_categories || []).map(sub => sub.nom)
          };
          if (alive) setResolvedCategory(data);
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [slug, category]);

  const handleCategoryClick = (category) => {
    setExpandedCategories(prev => {
      // Fermer toutes les autres catégories
      const newState = {};
      Object.keys(prev).forEach(key => {
        if (key !== category) {
          newState[key] = false;
        }
      });
      // Basculer l'état de la catégorie cliquée
      newState[category] = !prev[category];
      return newState;
    });
  };

  const handleSubcategoryChange = (category, subcategory) => {
    setSelectedSubcategories(prev => {
      const currentSubs = prev[category] || [];
      if (currentSubs.includes(subcategory)) {
        return {
          ...prev,
          [category]: currentSubs.filter(sub => sub !== subcategory)
        };
      }
      return {
        ...prev,
        [category]: [...currentSubs, subcategory]
      };
    });
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
  };

  const handleBudgetChange = (e) => {
    setBudget(e.target.value);
  };

  const handleTimingChange = (e) => {
    setTiming(e.target.value);
  };

  const handleGlobalCommentChange = (e) => {
    setGlobalComment(e.target.value);
  };

  const handleCustomDescriptionChange = (e) => {
    setCustomDescription(e.target.value);
  };

  const isFormValid = () => {
    // Vérifier si au moins une catégorie a des sous-catégories sélectionnées
    const hasSelectedSubcategories = Object.values(selectedSubcategories).some(subs => subs.length > 0);
    
    return (
      hasSelectedSubcategories &&
      budget &&
      timing &&
      location
    );
  };

  if (!resolvedCategory) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-xl">Chargement de la catégorie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-6 md:py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12 text-white">
          {resolvedCategory.title}
        </h1>

        <div className="max-w-[90vw] md:max-w-6xl mx-auto">
          {/* Image de fond */}
          <div className="relative mb-6 md:mb-8">
            <SafeImage
              src={resolvedCategory.image}
              alt={resolvedCategory.title}
              className="w-full h-[40vh] md:h-[50vh] object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg"></div>
          </div>

          {/* Description */}
          <div className="bg-gray-800 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
            <p className="text-gray-300 text-base md:text-lg">
              {resolvedCategory.description}
            </p>
          </div>

          {/* Formulaire */}
          <div className="space-y-6 md:space-y-8">
            {/* Section 1: Besoins */}
            <div className="bg-gray-800 rounded-lg p-4 md:p-6">
              <div className="flex items-center mb-4 md:mb-6">
                <div className="bg-primary text-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-lg md:text-xl font-bold mr-3 md:mr-4">
                  1
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">Sélectionnez vos besoins</h2>
              </div>

              <div className="space-y-4">
                {/* Mode de description */}
                <div className="flex space-x-4 mb-4 md:mb-6">
                  <button
                    onClick={() => setDescriptionMode('categories')}
                    className={`flex-1 py-2 md:py-3 px-3 md:px-4 rounded-lg text-sm md:text-base ${
                      descriptionMode === 'categories'
                        ? 'bg-primary text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Par catégories
                  </button>
                  <button
                    onClick={() => setDescriptionMode('freeText')}
                    className={`flex-1 py-2 md:py-3 px-3 md:px-4 rounded-lg text-sm md:text-base ${
                      descriptionMode === 'freeText'
                        ? 'bg-primary text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Avec vos propres mots
                  </button>
                </div>

                {descriptionMode === 'categories' ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <motion.div
                          whileHover={{ scale: 1.01 }}
                          className={`bg-gray-700 rounded-lg overflow-hidden cursor-pointer transition-all ${
                            selectedSubcategories[resolvedCategory.title]?.length > 0 ? 'ring-2 ring-primary' : ''
                          }`}
                          onClick={() => handleCategoryClick(resolvedCategory.title)}
                        >
                          <div className="relative h-[20vh] md:h-[25vh]">
                            <SafeImage
                              src={resolvedCategory.image}
                              alt={resolvedCategory.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                              <h3 className="text-white text-base md:text-lg font-semibold text-center px-4">
                                {resolvedCategory.title}
                              </h3>
                            </div>
                            <div className="absolute bottom-2 right-2">
                              <svg
                                className={`w-6 h-6 text-white transform transition-transform ${
                                  expandedCategories[resolvedCategory.title] ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </div>
                          </div>
                        </motion.div>

                        {expandedCategories[resolvedCategory.title] && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="bg-gray-700 rounded-lg p-3 md:p-4 ml-2 md:ml-4"
                          >
                            <div className="space-y-2">
                              {resolvedCategory.subcategories.map((subcategory, idx) => (
                                <label key={`${resolvedCategory.title}-${idx}`} className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedSubcategories[resolvedCategory.title]?.includes(subcategory) || false}
                                    onChange={() => handleSubcategoryChange(resolvedCategory.title, subcategory)}
                                    className="form-checkbox h-4 w-4 text-primary"
                                  />
                                  <span className="text-gray-300 text-sm md:text-base">{subcategory}</span>
                                </label>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>

                    {/* Commentaire global */}
                    <div className="mt-6 md:mt-8">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showGlobalComment}
                          onChange={() => setShowGlobalComment(!showGlobalComment)}
                          className="form-checkbox h-4 w-4 text-primary"
                        />
                        <span className="text-gray-300 text-sm md:text-base">Avez-vous des précisions à apporter ?</span>
                      </label>
                      
                      {showGlobalComment && (
                        <div className="mt-4">
                          <textarea
                            value={globalComment}
                            onChange={handleGlobalCommentChange}
                            placeholder="Ajoutez des précisions pour votre projet..."
                            className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 h-24 md:h-32 focus:ring-2 focus:ring-primary text-sm md:text-base"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <textarea
                    value={customDescription}
                    onChange={handleCustomDescriptionChange}
                    placeholder="Décrivez votre projet avec vos propres mots..."
                    className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 h-32 md:h-48 focus:ring-2 focus:ring-primary text-sm md:text-base"
                  />
                )}
              </div>
            </div>

            {/* Section 2: Dites-en-nous plus */}
            <div className="bg-gray-800 rounded-lg p-4 md:p-6">
              <div className="flex items-center mb-4 md:mb-6">
                <div className="bg-primary text-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-lg md:text-xl font-bold mr-3 md:mr-4">
                  2
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-white">Dites-en-nous plus</h2>
              </div>

              {/* Localisation */}
              <div className="mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-white">Lieu de l'opération</h3>
                <input
                  type="text"
                  value={location}
                  onChange={handleLocationChange}
                  placeholder="Entrez l'adresse..."
                  className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 focus:ring-2 focus:ring-primary text-sm md:text-base"
                />
              </div>

              {/* Budget et Timing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Budget */}
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-white">Budget estimé</h3>
                  <select
                    value={budget}
                    onChange={handleBudgetChange}
                    className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 focus:ring-2 focus:ring-primary h-[42px] md:h-[52px] text-sm md:text-base"
                  >
                    <option value="">Sélectionner un budget</option>
                    <option value="0-5000">0 - 5 000 €</option>
                    <option value="5000-10000">5 000 - 10 000 €</option>
                    <option value="10000-20000">10 000 - 20 000 €</option>
                    <option value="20000-50000">20 000 - 50 000 €</option>
                    <option value="50000+">50 000 € et plus</option>
                  </select>
                </div>

                {/* Timing */}
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-white">Quand souhaitez-vous commencer ?</h3>
                  <select
                    value={timing}
                    onChange={handleTimingChange}
                    className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 focus:ring-2 focus:ring-primary h-[42px] md:h-[52px] text-sm md:text-base"
                  >
                    <option value="">Sélectionner un timing</option>
                    <option value="immediate">Immédiatement</option>
                    <option value="1month">Dans 1 mois</option>
                    <option value="3months">Dans 3 mois</option>
                    <option value="6months">Dans 6 mois</option>
                    <option value="more">Plus tard</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bouton de soumission */}
            <button
              className={`w-full py-3 md:py-4 rounded-lg transition-colors font-semibold text-base md:text-lg ${
                isFormValid()
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
              }`}
              onClick={() => {
                if (isFormValid()) {
                  const devisData = {
                    category: category.title,
                    subcategories: Object.entries(selectedSubcategories)
                      .filter(([_, subs]) => subs.length > 0)
                      .map(([cat, subs]) => ({
                        category: cat,
                        subcategories: subs
                      })),
                    customDescription,
                    globalComment,
                    location,
                    budget,
                    timing
                  };
                  DevisGenerator.generateDevis(devisData);
                }
              }}
              disabled={!isFormValid()}
            >
              Demander un devis
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
